import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Icon } from '@iconify/react';
import { CtyButton, CtySpinner, HorizontalRulesAgGrid } from '@circuitry-ai/doc-ui';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { 
  useGetFgaGroupsQuery,
  useGetFgaResourceGroupsQuery,
  useAddFgaEntityResourceMutation,
  useRemoveFgaEntityResourceMutation,
  useAddFgaApplicationResourceMutation,
  useRemoveFgaApplicationResourceMutation,
  useAddFgaEntityTypeMutation,
  useRemoveFgaEntityTypeMutation
} from '../../../../shared/data/src/features/settings/settingsApiSlice';
import { handleApiError, handleApiSuccess } from '../fga-utils';
import { aggregateResourceGroupEntries, type PermissionLevel, type ResourceType, type AssignedGroupDetail } from '../fga-data-utils';

type AssignedGroup = AssignedGroupDetail;

interface EditResourceAccessModalProps {
  readonly isOpen: boolean;
  readonly onOpenChange: (isOpen: boolean) => void;
  readonly resourceName: string;
  readonly resourceId: string;
  readonly resourceType?: ResourceType;
  readonly onSuccess?: () => void;
}

const PERMISSION_LEVELS = [
  { value: 'read', label: 'Read', description: 'Read-only access' },
  { value: 'update', label: 'Update', description: 'Configure and edit' },
  { value: 'delete', label: 'Delete', description: 'Delete Access' }
] as const;

export { HorizontalRulesAgGrid } from '@circuitry-ai/doc-ui';

type AssignedGroupGridContext = {
  availablePermissions: ReadonlyArray<{ value: string; label: string; description: string }>;
  handlePermissionChange: (groupName: string, permission: PermissionLevel) => void;
  handleRemoveGroup: (groupName: string) => void;
  isLoading: boolean;
};

const EditModalRoleCell: React.FC<ICellRendererParams<AssignedGroup>> = (params) => {
  const d = params.data;
  if (!d) return null;
  return (
    <div className="leading-tight">
      <div className="text-sm font-medium text-foreground">{d.group}</div>
      <div className="text-xs text-default-500 mt-0.5">{d.userCount} users</div>
    </div>
  );
};

const EditModalPermissionsCell: React.FC<ICellRendererParams<AssignedGroup>> = (params) => {
  const d = params.data;
  const ctx = params.context as AssignedGroupGridContext | undefined;
  if (!d || !ctx) return null;
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2">
      {ctx.availablePermissions.map((level) => (
        <label
          key={level.value}
          className="flex items-center gap-2 cursor-pointer"
          htmlFor={`permission-${d.group}-${level.value}`}
          aria-label={`Set ${d.group} permission to ${level.label}`}
        >
          <input
            id={`permission-${d.group}-${level.value}`}
            type="checkbox"
            value={level.value}
            checked={d.permissions.includes(level.value as PermissionLevel)}
            onChange={() => ctx.handlePermissionChange(d.group, level.value as PermissionLevel)}
            disabled={ctx.isLoading}
            className="w-4 h-4 text-primary border-default-300 rounded focus:ring-primary disabled:opacity-50"
          />
          <div>
            <div className="text-sm font-medium text-foreground">{level.label}</div>
            <div className="text-xs text-default-500">{level.description}</div>
          </div>
        </label>
      ))}
    </div>
  );
};

const EditModalActionsCell: React.FC<ICellRendererParams<AssignedGroup>> = (params) => {
  const d = params.data;
  const ctx = params.context as AssignedGroupGridContext | undefined;
  if (!d || !ctx) return null;
  return (
    <button
      type="button"
      onClick={() => ctx.handleRemoveGroup(d.group)}
      disabled={ctx.isLoading}
      className="p-1.5 text-danger hover:bg-danger/10 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
      aria-label={`Remove ${d.group} from resource`}
    >
      <Icon icon="mdi:trash-can" className="w-4 h-4" />
    </button>
  );
};

const EDIT_RESOURCE_ASSIGNED_GROUPS_COL_DEFS: ColDef<AssignedGroup>[] = [
  {
    colId: 'role',
    headerName: 'Role',
    flex: 1,
    minWidth: 140,
    cellRenderer: EditModalRoleCell,
  },
  {
    colId: 'permissions',
    headerName: 'Permission Level',
    flex: 2,
    minWidth: 280,
    cellRenderer: EditModalPermissionsCell,
  },
  {
    colId: 'actions',
    headerName: 'Actions',
    field: 'actions' as any,
    width: 88,
    maxWidth: 100,
    flex: 0,
    cellRenderer: EditModalActionsCell,
  },
];

export function EditResourceAccessModal({ 
  isOpen, 
  onOpenChange, 
  resourceName,
  resourceId,
  resourceType = 'entity_resource',
  onSuccess
}: EditResourceAccessModalProps) {
  const [assignedGroups, setAssignedGroups] = useState<AssignedGroup[]>([]);
  const [isAddGroupsExpanded, setIsAddGroupsExpanded] = useState(false);
  const [selectedGroupsToAdd, setSelectedGroupsToAdd] = useState<string[]>([]);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const baselineAssignedGroupsRef = useRef<AssignedGroup[]>([]);
  // Tracks whether we have already initialised assignedGroups from the API for this open
  const isInitializedRef = useRef(false);

  // API hooks
  // Groups query – still needed for the "Add Roles" picker; refetches fresh on every modal mount
  const { data: groupsData, isFetching: groupsFetching } = useGetFgaGroupsQuery(undefined, { refetchOnMountOrArgChange: true });
  // Dedicated resource-groups query – directly returns assigned groups for this resource
  const { data: resourceGroupsData, isFetching: resourceGroupsFetching } = useGetFgaResourceGroupsQuery(
    { resourceType: resourceType ?? 'entity_resource', resourceId },
    { refetchOnMountOrArgChange: true }
  );
  const [addFgaEntityResource] = useAddFgaEntityResourceMutation();
  const [removeFgaEntityResource] = useRemoveFgaEntityResourceMutation();
  const [addFgaApplicationResource] = useAddFgaApplicationResourceMutation();
  const [removeFgaApplicationResource] = useRemoveFgaApplicationResourceMutation();
  const [addFgaEntityType] = useAddFgaEntityTypeMutation();
  const [removeFgaEntityType] = useRemoveFgaEntityTypeMutation();

  // Get available permissions based on resource type
  const availablePermissions = useMemo(() => {
    if (resourceType === 'application_resource') {
      return [{ value: 'view' as PermissionLevel, label: 'View', description: 'View access' }];
    }
    if (resourceType === 'entity_type') {
      return [{ value: 'create_instance' as PermissionLevel, label: 'Create Instance', description: 'Create instance access' }];
    }
    return PERMISSION_LEVELS;
  }, [resourceType]);

  const allGroups = groupsData?.data?.groups || [];

  // Filter groups that are not already assigned
  const availableGroups = useMemo(() => {
    const assignedGroupNames = new Set(assignedGroups.map(ag => ag.group));
    return allGroups.filter((group: any) => !assignedGroupNames.has(group.group));
  }, [allGroups, assignedGroups]);

  const filteredGroups = useMemo(() => {
    if (groupSearchTerm.trim()) {
      return availableGroups.filter((group: any) => group.group.toLowerCase().includes(groupSearchTerm.toLowerCase()));
    }
    return availableGroups;
  }, [availableGroups, groupSearchTerm]);

  // Initialise assignedGroups once when the resource-groups API response arrives.
  // The component is conditionally rendered (mounts on open, unmounts on close), so
  // isInitializedRef resets automatically on each fresh mount – no cleanup needed.
  useEffect(() => {
    if (!isInitializedRef.current && resourceGroupsData && !resourceGroupsFetching) {
      const entries: Array<{ group: string; relation: string }> = resourceGroupsData.data?.groups || [];
      const memberCountMap = new Map<string, number>(
        (groupsData?.data?.groups || []).map((g: any) => [g.group as string, (g.member_count as number) || 0])
      );
      const freshAssigned = aggregateResourceGroupEntries(entries, memberCountMap);
      baselineAssignedGroupsRef.current = freshAssigned;
      setAssignedGroups(freshAssigned);
      isInitializedRef.current = true;
    }
  }, [resourceGroupsData, resourceGroupsFetching, groupsData]);

  // Helper function to update permissions for a group
  const updateGroupPermissions = (
    group: AssignedGroup,
    permission: PermissionLevel
  ): AssignedGroup => {
    const hasPermission = group.permissions.includes(permission);
    const newPermissions = hasPermission
      ? group.permissions.filter(p => p !== permission)
      : [...group.permissions, permission];
    
    return {
      ...group,
      permissions: newPermissions
    };
  };

  const handlePermissionChange = (groupName: string, permission: PermissionLevel) => {
    setAssignedGroups(prev => 
      prev.map(ag => {
        if (ag.group !== groupName) return ag;
        
        // For application resources and entity types, only allow one permission
        if (resourceType === 'application_resource' || resourceType === 'entity_type') {
          // Toggle: if already has permission, remove it; otherwise, set it as the only permission
          const hasPermission = ag.permissions.includes(permission);
          return {
            ...ag,
            permissions: hasPermission ? [] : [permission]
          };
        }
        
        // For entity resources, allow multiple permissions
        return updateGroupPermissions(ag, permission);
      })
    );
  };

  const handleRemoveGroup = (groupName: string) => {
    setAssignedGroups(prev => prev.filter(ag => ag.group !== groupName));
  };

  const handleGroupToggle = (groupName: string) => {
    setSelectedGroupsToAdd(prev => 
      prev.includes(groupName) ? prev.filter(name => name !== groupName) : [...prev, groupName]
    );
  };

  const handleAddGroups = () => {
    if (selectedGroupsToAdd.length === 0) return;

    // Default permission based on resource type
    let defaultPermission: PermissionLevel[];
    if (resourceType === 'application_resource') {
      defaultPermission = ['view'];
    } else if (resourceType === 'entity_type') {
      defaultPermission = ['create_instance'];
    } else {
      defaultPermission = ['read'];
    }
    
    const newGroups: AssignedGroup[] = selectedGroupsToAdd.map(groupName => ({
      group: groupName,
      permissions: defaultPermission,
      userCount: 0 // User count will be updated when data is refreshed
    }));

    setAssignedGroups(prev => [...prev, ...newGroups]);
    setSelectedGroupsToAdd([]);
    setIsAddGroupsExpanded(false);
    setGroupSearchTerm('');
  };

  // Helper function to remove permissions for a group
  const removeGroupPermissions = async (groupName: string, permissions: PermissionLevel[]) => {
    if (resourceType === 'application_resource') {
      // Application resources only have 'view' permission
      await removeFgaApplicationResource({
        group: groupName,
        application_resource: resourceId,
        relation: 'view'
      }).unwrap();
    } else if (resourceType === 'entity_type') {
      // Entity types only have 'create_instance' permission
      await removeFgaEntityType({
        group: groupName,
        entity_type: resourceId,
        relation: 'create_instance'
      }).unwrap();
    } else {
      // Entity resources have read, update, delete permissions
      for (const permission of permissions) {
        await removeFgaEntityResource({
          group: groupName,
          entity_resource: resourceId,
          relation: permission
        }).unwrap();
      }
    }
  };

  // Helper function to add permissions for a group
  const addGroupPermissions = async (groupName: string, permissions: PermissionLevel[]) => {
    if (resourceType === 'application_resource') {
      // Application resources only have 'view' permission
      await addFgaApplicationResource({
        group: groupName,
        application_resource: resourceId,
        relation: 'view'
      }).unwrap();
    } else if (resourceType === 'entity_type') {
      // Entity types only have 'create_instance' permission
      await addFgaEntityType({
        group: groupName,
        entity_type: resourceId,
        relation: 'create_instance'
      }).unwrap();
    } else {
      // Entity resources have read, update, delete permissions
      for (const permission of permissions) {
        await addFgaEntityResource({
          group: groupName,
          entity_resource: resourceId,
          relation: permission
        }).unwrap();
      }
    }
  };

  // Helper function to handle removed groups
  const handleRemovedGroups = async (initialGroupNames: Set<string>, currentGroupNames: Set<string>) => {
    const baseline = baselineAssignedGroupsRef.current;
    for (const groupName of initialGroupNames) {
      if (!currentGroupNames.has(groupName)) {
        const groupToRemove = baseline.find(ag => ag.group === groupName);
        if (groupToRemove?.permissions) {
          await removeGroupPermissions(groupName, groupToRemove.permissions);
        }
      }
    }
  };

  // Helper function to handle existing groups with permission changes
  const handleExistingGroups = async (
    assignedGroup: AssignedGroup,
    initialPermissions: PermissionLevel[],
    currentPermissions: PermissionLevel[]
  ) => {
    // Remove permissions that are no longer selected
    const permissionsToRemove = initialPermissions.filter(p => !currentPermissions.includes(p));
    if (permissionsToRemove.length > 0) {
      await removeGroupPermissions(assignedGroup.group, permissionsToRemove);
    }
    
    // Add new permissions
    const permissionsToAdd = currentPermissions.filter(p => !initialPermissions.includes(p));
    if (permissionsToAdd.length > 0) {
      await addGroupPermissions(assignedGroup.group, permissionsToAdd);
    }
  };

  // Helper function to handle new groups
  const handleNewGroups = async (assignedGroup: AssignedGroup) => {
    await addGroupPermissions(assignedGroup.group, assignedGroup.permissions);
  };

  const handleSaveChanges = async () => {
    if (!hasChanges) return;
    
    setIsLoading(true);
    
    try {
      const baseline = baselineAssignedGroupsRef.current;
      const initialGroupNames = new Set(baseline.map(ag => ag.group));
      const currentGroupNames = new Set(assignedGroups.map(ag => ag.group));

      // Remove groups that are no longer assigned
      await handleRemovedGroups(initialGroupNames, currentGroupNames);

      // Handle existing and new groups
      for (const assignedGroup of assignedGroups) {
        const wasInitiallyAssigned = initialGroupNames.has(assignedGroup.group);
        const initialGroup = baseline.find(ag => ag.group === assignedGroup.group);
        const initialPermissions = initialGroup?.permissions || [];
        const currentPermissions = assignedGroup.permissions;
        
        if (wasInitiallyAssigned) {
          await handleExistingGroups(assignedGroup, initialPermissions, currentPermissions);
        } else {
          await handleNewGroups(assignedGroup);
        }
      }

      handleApiSuccess('Resource access updated successfully');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      handleApiError(error, 'update resource access');
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges = useMemo(() => {
    const baseline = baselineAssignedGroupsRef.current;
    if (assignedGroups.length !== baseline.length) return true;
    
    for (const assignedGroup of assignedGroups) {
      const initialGroup = baseline.find(ag => ag.group === assignedGroup.group);
      if (!initialGroup) return true;
      
      // Compare permissions arrays
      const sortedInitialPerms = [...initialGroup.permissions].sort((a, b) => a.localeCompare(b));
      const sortedCurrentPerms = [...assignedGroup.permissions].sort((a, b) => a.localeCompare(b));
      const initialPerms = sortedInitialPerms.join(',');
      const currentPerms = sortedCurrentPerms.join(',');
      if (initialPerms !== currentPerms) return true;
    }
    
    return false;
  }, [assignedGroups]);

  if (!isOpen) return null;

  return (
    <>
      <button 
        className="fixed inset-0 bg-black bg-opacity-50 z-[42]" 
        onClick={() => onOpenChange(false)} 
      />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-content1 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b border-default-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  Edit Resource Access
                </h2>
                <p className="text-sm text-default-500 mt-1">
                  Manage role assignments and permission levels for "{resourceName}". You can add or remove role access and modify permission levels for each role.
                </p>
              </div>
              <button 
                onClick={() => onOpenChange(false)}
                className="p-2 hover:bg-default-100 rounded-full"
              >
                <Icon icon="mdi:close" className="w-5 h-5 text-default-500" />
              </button>
            </div>
          </div>

          {groupsFetching || resourceGroupsFetching ? (
            <div className="flex-1 flex items-center justify-center py-16">
              <CtySpinner size="md" />
            </div>
          ) : (
          <>
          {/* Assigned Groups Section */}
          <div className="p-6 border-b border-default-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-foreground">Assigned Roles</h3>
                <p className="text-sm text-default-500">{assignedGroups.length} roles assigned</p>
              </div>
              <div className="flex">
                <CtyButton
                  size="sm"
                  color="primary"
                  onPress={() => setIsAddGroupsExpanded(!isAddGroupsExpanded)}
                  startContent={<Icon icon="mdi:plus" className="w-4 h-4" />}
                  isDisabled={isLoading}
                >
                  Add Roles
                </CtyButton>
              </div>
            </div>

            {assignedGroups.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-default-200 rounded-lg">
                <Icon icon="mdi:account-group-outline" className="w-12 h-12 text-default-400 mx-auto mb-2" />
                <p className="text-default-500 font-medium">No groups assigned to this resource</p>
                <p className="text-default-400 text-sm">Add groups to grant access</p>
              </div>
            ) : (
              <HorizontalRulesAgGrid<AssignedGroup>
                rowData={assignedGroups}
                columnDefs={EDIT_RESOURCE_ASSIGNED_GROUPS_COL_DEFS}
                getRowId={(p) => p.data.group}
                gridPersistenceKey="edit-resource-access-modal-grid"
                dense
                context={
                  {
                    availablePermissions,
                    handlePermissionChange,
                    handleRemoveGroup,
                    isLoading,
                  } satisfies AssignedGroupGridContext
                }
              />
            )}
          </div>

          {/* Add Groups Section */}
          {isAddGroupsExpanded && (
            <div className="p-6 border-b border-default-200 bg-default-100">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-foreground">Add Roles</h3>
                  <p className="text-sm text-default-500">Select Roles to grant access to this resource</p>
                </div>

                {/* Search Bar */}
                <div className="relative">
                  <Icon icon="mdi:magnify" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-default-400" />
                  <input
                    type="text"
                    placeholder="Search roles..."
                    value={groupSearchTerm}
                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                    disabled={isLoading}
                    className="w-full pl-10 pr-4 py-2 border border-default-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>

                {/* Groups List */}
                <div className="max-h-48 overflow-y-auto space-y-2">
                  {filteredGroups.map((group: any) => (
                    <label
                      key={group.group}
                      className="flex items-center space-x-3 p-3 hover:bg-content1 rounded-lg cursor-pointer border border-default-200"
                      htmlFor={`group-${group.group}`}
                      aria-label={`Select group ${group.group}`}
                    >
                      <input
                        id={`group-${group.group}`}
                        type="checkbox"
                        checked={selectedGroupsToAdd.includes(group.group)}
                        onChange={() => handleGroupToggle(group.group)}
                        disabled={isLoading}
                        className="w-4 h-4 text-primary border-default-300 rounded focus:ring-primary disabled:opacity-50"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-foreground">{group.group}</div>
                        <div className="text-sm text-default-500">
                          {group.member_count || 0} users
                        </div>
                      </div>
                    </label>
                  ))}
                </div>

                {/* Add Groups Actions */}
                <div className="flex justify-end gap-3 pt-4">
                  <CtyButton
                    variant="bordered"
                    onPress={() => {
                      setIsAddGroupsExpanded(false);
                      setSelectedGroupsToAdd([]);
                      setGroupSearchTerm('');
                    }}
                    isDisabled={isLoading}
                  >
                    Cancel
                  </CtyButton>
                  <CtyButton
                    color="primary"
                    onPress={handleAddGroups}
                    isDisabled={selectedGroupsToAdd.length === 0 || isLoading}
                    isLoading={isLoading}
                  >
                    Add {selectedGroupsToAdd.length} roles
                  </CtyButton>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-6 border-t border-default-200 flex justify-end gap-3">
            <CtyButton
              variant="bordered"
              onPress={() => onOpenChange(false)}
              isDisabled={isLoading}
            >
              Cancel
            </CtyButton>
            <CtyButton
              color="primary"
              onPress={handleSaveChanges}
              isDisabled={!hasChanges || isLoading}
              isLoading={isLoading}
            >
              Save Changes
            </CtyButton>
          </div>
          </>
          )}
        </div>
      </div>
    </>
  );
}

export default EditResourceAccessModal;
